import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";

// Picking an event used to mark its pin and leave the camera where it was,
// so an event off the edge of the viewport got selected invisibly - the
// list said one thing and the map showed another.
//
// Only an actual pick calls this. Hovering a row or a route does not, so
// the camera never chases the mouse.

// below this the pin is centred but still too far out to read
const EVENT_FOCUS_MIN_ZOOM = 15;

// within this share of the viewport of dead centre, it is centred enough -
// re-picking the event already under the crosshair would otherwise animate
// the map for no visible reason
const EVENT_CENTRED_FRACTION = 0.02;

// Focusing an event is a round trip: you go in to read one pin and then you
// want the trip back. Closing the event popup used to leave the camera
// parked at zoom 15 over a junction, with the rest of the route off screen,
// and the only way back was to zoom out by hand and go looking for it.
//
// So the view is remembered on the way in and put back on the way out. Only
// the FIRST focus of a run saves it: stepping from one event to the next is
// still inside the same visit, and saving each time would make "back" mean
// the previous pin rather than the trip.
export const createEventCamera = (map: any) => {
  let viewBeforeFocus: {
    center: { lat: number; lng: number };
    zoom: number;
  } | null = null;

  const focusOn = (point: VehicleMapDataPoint) => {
    if (!viewBeforeFocus) {
      const centre = map.getCenter?.();
      const zoom = map.getZoom?.();
      if (centre && typeof zoom === "number")
        viewBeforeFocus = {
          center: { lat: centre.lat(), lng: centre.lng() },
          zoom,
        };
    }
    centreMapOnEvent(map, point);
  };

  // back to where the first focus started from. A no-op if nothing was
  // saved, so closing a popup that never moved the camera leaves it alone.
  const restore = () => {
    if (!viewBeforeFocus) return;
    const { center, zoom } = viewBeforeFocus;
    viewBeforeFocus = null;
    map.setZoom(zoom);
    map.panTo(center);
  };

  // something else has taken the camera - a trip opening or closing frames
  // its own route - so the saved view is no longer anywhere worth returning
  // to and must not be restored over whatever replaced it.
  const forget = () => {
    viewBeforeFocus = null;
  };

  return { focusOn, restore, forget };
};

const centreMapOnEvent = (map: any, point: VehicleMapDataPoint) => {
  const position = { lat: point.latitude, lng: point.longitude };
  const viewport = map.getBounds?.();
  const centre = map.getCenter?.();

  if (viewport && centre) {
    const ne = viewport.getNorthEast();
    const sw = viewport.getSouthWest();
    const latSpan = ne.lat() - sw.lat();
    const lngSpan = ne.lng() - sw.lng();
    const alreadyCentred =
      Math.abs(position.lat - centre.lat()) <
        latSpan * EVENT_CENTRED_FRACTION &&
      Math.abs(position.lng - centre.lng()) < lngSpan * EVENT_CENTRED_FRACTION;
    if (!alreadyCentred) map.panTo(position);
  } else {
    // no viewport yet - the map is still coming up
    map.panTo(position);
  }

  if ((map.getZoom?.() ?? 0) < EVENT_FOCUS_MIN_ZOOM)
    map.setZoom(EVENT_FOCUS_MIN_ZOOM);
};
